<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import {
  useFunctionGetter,
  useMapGetter,
  useStore,
} from 'dashboard/composables/store';

import TeamForm from '../TeamForm.vue';
import TeamRoutingSettings from '../TeamRoutingSettings.vue';
import AgentSelector from '../AgentSelector.vue';
import PageHeader from '../../SettingsSubPageHeader.vue';
import Spinner from 'shared/components/Spinner.vue';

const { t } = useI18n();
const route = useRoute();
const store = useStore();

const teamId = computed(() => route.params.teamId);
const teamGetter = useFunctionGetter('teams/getTeam', teamId);
const teamMembersGetter = useFunctionGetter(
  'teamMembers/getTeamMembers',
  teamId
);
const agentList = useMapGetter('agents/getAgents');
const teamUIFlags = useMapGetter('teams/getUIFlags');
const teamMembersUIFlags = useMapGetter('teamMembers/getUIFlags');

const selectedAgents = ref([]);
const isSavingTeam = ref(false);
const isSavingAgents = ref(false);

const teamData = computed(() => teamGetter.value || {});
const showTeamForm = computed(
  () => Boolean(teamData.value.id) && !teamUIFlags.value.isFetching
);
const isLoadingMembers = computed(
  () => teamMembersUIFlags.value.isFetching || !teamData.value.id
);

const updateSelectedAgents = agents => {
  selectedAgents.value = [...agents];
};

const updateTeam = async data => {
  isSavingTeam.value = true;
  try {
    await store.dispatch('teams/update', {
      id: teamId.value,
      ...data,
    });
    useAlert(t('TEAMS_SETTINGS.TEAM_FORM.UPDATE_SUCCESS'));
  } catch (error) {
    useAlert(t('TEAMS_SETTINGS.TEAM_FORM.ERROR_MESSAGE'));
  } finally {
    isSavingTeam.value = false;
  }
};

const saveAgents = async () => {
  if (!selectedAgents.value.length) {
    useAlert(t('TEAMS_SETTINGS.ADD.AGENT_VALIDATION_ERROR'));
    return;
  }

  isSavingAgents.value = true;
  try {
    await store.dispatch('teamMembers/update', {
      teamId: teamId.value,
      agentsList: selectedAgents.value,
    });
    await store.dispatch('teams/get');
    useAlert(t('TEAMS_SETTINGS.AGENTS.UPDATE_SUCCESS'));
  } catch (error) {
    useAlert(t('TEAMS_SETTINGS.AGENTS.UPDATE_ERROR'));
  } finally {
    isSavingAgents.value = false;
  }
};

onMounted(async () => {
  try {
    await Promise.all([
      store.dispatch('agents/get'),
      store.dispatch('teamMembers/get', { teamId: teamId.value }),
    ]);
    selectedAgents.value = teamMembersGetter.value.map(agent => agent.id);
  } catch (error) {
    selectedAgents.value = [];
  }
});
</script>

<template>
  <div class="h-full w-full overflow-y-auto">
    <div class="mx-auto flex w-full max-w-5xl flex-col gap-6 px-6 py-6">
      <PageHeader
        :header-title="
          $t('TEAMS_SETTINGS.EDIT_PAGE.TITLE', { teamName: teamData.name })
        "
        :header-content="$t('TEAMS_SETTINGS.EDIT_PAGE.DESCRIPTION')"
      />

      <div v-if="showTeamForm" class="flex flex-col gap-6">
        <section class="rounded-xl border border-n-weak bg-n-surface-1 p-6">
          <div class="mb-6">
            <h3 class="text-heading-2 text-n-slate-12">
              {{ $t('TEAMS_SETTINGS.EDIT_PAGE.DETAILS_TITLE') }}
            </h3>
            <p class="mt-1 text-body-main text-n-slate-11">
              {{ $t('TEAMS_SETTINGS.EDIT_PAGE.DETAILS_DESCRIPTION') }}
            </p>
          </div>
          <TeamForm
            :on-submit="updateTeam"
            :submit-in-progress="isSavingTeam"
            :submit-button-text="$t('TEAMS_SETTINGS.EDIT_PAGE.SAVE_DETAILS')"
            :form-data="teamData"
          />
        </section>

        <section class="rounded-xl border border-n-weak bg-n-surface-1 p-6">
          <div>
            <h3 class="text-heading-2 text-n-slate-12">
              {{ $t('TEAMS_SETTINGS.EDIT_PAGE.AGENTS_TITLE') }}
            </h3>
            <p class="mt-1 text-body-main text-n-slate-11">
              {{ $t('TEAMS_SETTINGS.EDIT_PAGE.AGENTS_DESCRIPTION') }}
            </p>
          </div>

          <form class="mt-6" @submit.prevent="saveAgents">
            <div v-if="isLoadingMembers" class="flex justify-center py-8">
              <Spinner class="text-n-blue-11" />
            </div>
            <AgentSelector
              v-else
              :agent-list="agentList"
              :selected-agents="selectedAgents"
              :update-selected-agents="updateSelectedAgents"
              :is-working="isSavingAgents"
              embedded
              :submit-button-text="$t('TEAMS_SETTINGS.EDIT_PAGE.SAVE_AGENTS')"
            />
          </form>
        </section>

        <section class="rounded-xl border border-n-weak bg-n-surface-1 p-6">
          <div class="mb-6">
            <h3 class="text-heading-2 text-n-slate-12">
              {{ $t('TEAMS_SETTINGS.EDIT_PAGE.ROUTING_TITLE') }}
            </h3>
            <p class="mt-1 text-body-main text-n-slate-11">
              {{ $t('TEAMS_SETTINGS.EDIT_PAGE.ROUTING_DESCRIPTION') }}
            </p>
          </div>
          <TeamRoutingSettings :team="teamData" embedded />
        </section>
      </div>

      <div v-else class="flex justify-center py-8">
        <Spinner />
      </div>
    </div>
  </div>
</template>
